/**
 * Axios 统一配置
 * 包括请求拦截器和响应拦截器，用于统一处理ID转换
 * 直接在axios默认实例上添加拦截器，这样所有import axios的地方都会自动使用
 */
import axios from 'axios'
import { convertIdsToString } from './idHelper'
import { encodePayloadForHeader } from './antiCrawler'
import { handleWxMpOAuthRequiredResponse } from '@/shared/utils/wxMpOAuth'
import {
  ERR_AUTH_EXPIRED,
  ERR_SESSION_NOT_FOUND,
  normalizeApiCode,
} from '@/shared/utils/apiErrorCodes'
import { AUTH_TOKEN_HEADER, clearAuthSession, getAuthToken } from '@/shared/utils/auth'
import { emitAuthChanged } from '@/shared/utils/authEvents'

function restoreUrlIfValid(config, originalUrl) {
  if (originalUrl && originalUrl !== 'undefined' && originalUrl !== 'null') {
    config.url = originalUrl
    return true
  }
  return false
}

// 确保 axios 没有设置默认的 baseURL（使用相对路径，走代理）
if (axios.defaults.baseURL) {
  console.warn('检测到 axios.defaults.baseURL 已设置:', axios.defaults.baseURL, '将清除以确保使用相对路径')
  axios.defaults.baseURL = undefined
}

// 请求拦截器：统一处理请求参数中的ID字段
axios.interceptors.request.use(
  (config) => {
    const originalUrl = config.url

    if (!config.url || config.url === 'undefined' || config.url === 'null') {
      console.error('Axios 拦截器 - URL 无效', {
        url: config.url,
        originalUrl,
        baseURL: config.baseURL,
      })
      if (!restoreUrlIfValid(config, originalUrl)) {
        return Promise.reject(new Error('请求 URL 无效'))
      }
    }

    if (
      config.data &&
      !(typeof FormData !== 'undefined' && config.data instanceof FormData)
    ) {
      config.data = convertIdsToString(config.data, true)
    }

    if (config.params) {
      config.params = convertIdsToString(config.params, true)
    }

    if (!config.url || config.url === 'undefined' || config.url === 'null') {
      console.error('Axios 拦截器 - 最终检查 URL 无效', {
        url: config.url,
        baseURL: config.baseURL,
        originalUrl,
      })
      if (!restoreUrlIfValid(config, originalUrl)) {
        return Promise.reject(new Error('请求 URL 无效'))
      }
    }

    if (config.baseURL) {
      console.warn('Axios 拦截器 - 检测到 config.baseURL，将清除:', config.baseURL)
      delete config.baseURL
    }

    if (
      typeof config.url !== 'string' ||
      !config.url ||
      config.url === 'undefined' ||
      config.url === 'null'
    ) {
      console.error('Axios 拦截器 - URL 类型无效', {
        url: config.url,
        type: typeof config.url,
        originalUrl,
      })
      if (!restoreUrlIfValid(config, originalUrl)) {
        return Promise.reject(new Error(`无效的请求 URL: ${config.url}`))
      }
    }

    if (config.url && (config.url.startsWith('http') || config.url.startsWith('//'))) {
      console.warn('Axios 拦截器 - URL 是绝对路径，可能不走代理:', config.url)
    }

    try {
      const acPayload = encodePayloadForHeader()
      if (acPayload) {
        config.headers = config.headers || {}
        config.headers['X-Anti-Crawler'] = acPayload
      }
    } catch {
      // 不影响正常请求
    }

    config.headers = config.headers || {}
    const existingHeader = config.headers[AUTH_TOKEN_HEADER]
    const hasHeader =
      typeof existingHeader === 'string' && existingHeader.trim().length > 0
    if (!hasHeader) {
      const token = getAuthToken()
      if (token) {
        config.headers[AUTH_TOKEN_HEADER] = token
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截器：10102 整页 OAuth；统一处理响应数据中的 ID 字段
axios.interceptors.response.use(
  (response) => {
    const body = response.data
    if (handleWxMpOAuthRequiredResponse(body)) {
      return new Promise(() => {})
    }
    const code =
      body && typeof body === 'object' && 'code' in body ? normalizeApiCode(body.code) : null
    if (code === ERR_SESSION_NOT_FOUND || code === ERR_AUTH_EXPIRED) {
      clearAuthSession()
      emitAuthChanged()
    }
    if (response.data) {
      response.data = convertIdsToString(response.data, true)
    }
    return response
  },
  (error) => Promise.reject(error)
)

export default axios
